import { PersonalLoanQuestion } from '@hb/domain/steps/financial-obligation';
import { FinancialObligationsStoreService } from '@hb/modules/financial-obligations/financial-obligations-store.service';
import { FieldSubject } from '@hb/domain/field-subject';
import { PersonalLoanBuilder, money } from '@hb/builder';
import { AbstractStoreInjectOption } from '@hb/services/store/abstract-store-inject.service';

import { inject, Injectable, OnDestroy } from '@angular/core';
import { Subscription, filter } from 'rxjs';

import { Money } from '@ap/interface-bspc-core';
import { PersonalLoan } from '@ap/ap-interface-apix-hypotheken-berekening';

@Injectable({
    providedIn: 'root'
})
export class PersonalLoanStoreService extends AbstractStoreInjectOption<PersonalLoanQuestion> implements OnDestroy {
    protected readonly store = inject(FinancialObligationsStoreService);
    protected readonly lastQuestion = PersonalLoanQuestion.SUMMARY;

    private personalLoanSubscription: Subscription;
    private readonly monthlyPaymentSubject: FieldSubject<Money>;
    private readonly totalAmountSubject: FieldSubject<Money>;

    constructor() {
        super();
        this.init();

        const initialValues = this.getInitialValues();
        this.monthlyPaymentSubject = new FieldSubject<Money>(initialValues?.monthly_amount);
        this.totalAmountSubject = new FieldSubject<Money>(initialValues?.original_amount);

        this.subscriptions();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.personalLoanSubscription.unsubscribe();
        this.activeQuestionSubject.unsubscribe();
        this.monthlyPaymentSubject.unsubscribe();
        this.totalAmountSubject.unsubscribe();
    }

    update(): void {
        this.store.setPersonalLoan(
            new PersonalLoanBuilder()
                .withMonthlyAmount(this.monthlyPaymentSubject.value)
                .withOriginalAmount(this.totalAmountSubject.value)
                .build()
        );
    }

    setMonthlyPayment(amount: string): void {
        this.monthlyPaymentSubject.next(money(amount));
    }

    getMonthlyPayment(): Money {
        return this.monthlyPaymentSubject.value;
    }

    setTotalAmount(amount: string): void {
        this.totalAmountSubject.next(money(amount));
    }

    getTotalAmount(): Money {
        return this.totalAmountSubject.value;
    }

    getInitialValues(): PersonalLoan {
        return this.store.getFinancialObligationState()?.personal_loan;
    }

    private subscriptions(): void {
        this.subscribePersonalLoanOption();
        this.subscribeFieldSubjects();
    }

    private subscribePersonalLoanOption() {
        this.personalLoanSubscription = this.store.getFinancialObligationsOptions().subscribe((options) => {
            if (!options.personalLoan) {
                this.monthlyPaymentSubject.next(null);
                this.totalAmountSubject.next(null);
            }
        });
    }

    private subscribeFieldSubjects() {
        this.monthlyPaymentSubject
            .asObservable()
            .pipe(
                filter(() => this.totalAmountSubject.value !== null),
                filter(() => this.totalAmountSubject.value !== undefined)
            )
            .subscribe(() => this.update());
        this.totalAmountSubject
            .asObservable()
            .pipe(
                filter(() => this.monthlyPaymentSubject.value !== null),
                filter(() => this.monthlyPaymentSubject.value !== undefined)
            )
            .subscribe(() => this.update());
    }
}
